import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import EnrollmentModal from '../Components/EnrollmentModal';
import LoginPromptModal from '../Components/LoginPromptModal';
import './CourseDetail.css';

export default function CourseDetail() {
  const { id } = useParams();
  const { user, token } = useAuth();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);

  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const SERVER_URL = 'https://api.avtechbmt.cloud';
  const API_BASE_URL = `${SERVER_URL}/api/v1`;

  useEffect(() => {
    const fetchCourseDetail = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/courses/${id}`);
        if (!response.ok) throw new Error('Course details not found');
        const data = await response.json();
        const courseData = data.data || data;
        setCourse(courseData);

        if (user && token) {
          const myCoursesRes = await fetch(`${API_BASE_URL}/user/my-courses`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (myCoursesRes.ok) {
            const myCoursesData = await myCoursesRes.json();
            const enrolledList = myCoursesData.data || [];
            setIsEnrolled(enrolledList.some((c) => c._id === id));
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCourseDetail();
  }, [id, user, token, API_BASE_URL]);

  const getImageUrl = (path) => {
    if (!path) return '';
    const cleanPath = path.replace(/\\/g, '/').replace(/^\/+/, '');
    return `${SERVER_URL}/${cleanPath}`;
  };

  const handleEnroll = async () => {
    if (!token) { setIsLoginModalOpen(true); return; }
    setActionLoading(true); setMessage(''); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/user/courses/${id}/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Enrollment failed');
      setIsEnrolled(true);
      setMessage(data.message);
      setIsEnrollModalOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnenroll = async () => {
    if (!token) { setIsLoginModalOpen(true); return; }
    setActionLoading(true); setMessage(''); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/user/courses/${id}/unenroll`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Unenrollment failed');
      setIsEnrolled(false);
      setMessage(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="course-detail-page">
        <div className="course-detail-page__grain" aria-hidden="true" />
        <div className="course-detail-page__state">
          <div className="course-detail-page__state-tag">Loading</div>
          <div className="course-detail-page__state-title">Fetching course details…</div>
          <div className="course-detail-page__loader-bar">
            <div className="course-detail-page__loader-bar-fill" />
          </div>
        </div>
      </div>
    );
  }

  if (error && !course) {
    return (
      <div className="course-detail-page">
        <div className="course-detail-page__grain" aria-hidden="true" />
        <div className="course-detail-page__state">
          <div className="course-detail-page__state-tag">Error 404</div>
          <h2 className="course-detail-page__state-title">
            Course <em>unavailable.</em>
          </h2>
          <p className="course-detail-page__state-message">{error}</p>
          <Link to="/courses" className="course-detail-page__btn course-detail-page__btn--primary">
            <span>Back to Courses</span>
            <svg className="course-detail-page__btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      </div>
    );
  }

  const totalModules = course.syllabus?.length || 0;

  return (
    <div className="course-detail-page">
      <div className="course-detail-page__grain" aria-hidden="true" />

      <EnrollmentModal
        isOpen={isEnrollModalOpen}
        onClose={() => setIsEnrollModalOpen(false)}
        courseTitle={course?.title}
      />
      <LoginPromptModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />

      {/* HERO */}
      <section className="course-detail-page__hero">
        <div className="course-detail-page__hero-inner">
          <Link to="/courses" className="course-detail-page__back-link">
            <svg className="course-detail-page__back-arrow" width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M13 8H3M7 12L3 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>All Courses</span>
          </Link>

          <div className="course-detail-page__category-wrapper">
            {course.category && (
              <span className="course-detail-page__tag">{course.category}</span>
            )}
            {course.level && (
              <span className="course-detail-page__tag course-detail-page__tag--level">
                {course.level}
              </span>
            )}
          </div>

          <h1 className="course-detail-page__title">{course.title}</h1>
          <p className="course-detail-page__sub">{course.description}</p>

          <div className="course-detail-page__hero-stats">
            {course.duration && (
              <div className="course-detail-page__hero-stat">
                <span className="course-detail-page__hero-stat-value">{course.duration}</span>
                <span className="course-detail-page__hero-stat-label">Duration</span>
              </div>
            )}
            <div className="course-detail-page__hero-stat">
              <span className="course-detail-page__hero-stat-value">
                {String(course.projects || 0).padStart(2, '0')}
              </span>
              <span className="course-detail-page__hero-stat-label">Projects</span>
            </div>
            {totalModules > 0 && (
              <div className="course-detail-page__hero-stat">
                <span className="course-detail-page__hero-stat-value">
                  {String(totalModules).padStart(2, '0')}
                </span>
                <span className="course-detail-page__hero-stat-label">Modules</span>
              </div>
            )}
            {course.internship && (
              <div className="course-detail-page__hero-stat">
                <span className="course-detail-page__hero-stat-value">{course.internship}</span>
                <span className="course-detail-page__hero-stat-label">Internship</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="course-detail-page__body">
        <div className="course-detail-page__container">
          <div className="course-detail-page__layout">

            {/* MAIN CONTENT */}
            <div className="course-detail-page__main">
              {course.imageUrl && (
                <div className="course-detail-page__image-wrap">
                  <img
                    src={getImageUrl(course.imageUrl)}
                    alt={course.title}
                    className="course-detail-page__image"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x450?text=Image+Load+Failed';
                    }}
                  />
                </div>
              )}

              <section className="course-detail-page__section">
                <div className="course-detail-page__section-head">
                  <h2 className="course-detail-page__section-title">Overview</h2>
                  <span className="course-detail-page__section-num">01 / About</span>
                </div>
                <p className="course-detail-page__description">
                  {course.detailedDescription || course.description}
                </p>
              </section>

              {course.features && course.features.length > 0 && (
                <section className="course-detail-page__section">
                  <div className="course-detail-page__section-head">
                    <h2 className="course-detail-page__section-title">Key Highlights</h2>
                    <span className="course-detail-page__section-num">02 / Skills</span>
                  </div>
                  <ul className="course-detail-page__features">
                    {course.features.map((feature, idx) => (
                      <li key={idx} className="course-detail-page__feature">
                        <span className="course-detail-page__feature-check">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {course.syllabus && course.syllabus.length > 0 && (
                <section className="course-detail-page__section">
                  <div className="course-detail-page__section-head">
                    <h2 className="course-detail-page__section-title">Syllabus & Modules</h2>
                    <span className="course-detail-page__section-num">03 / Curriculum</span>
                  </div>
                  <div className="course-detail-page__syllabus-list">
                    {course.syllabus.map((item, idx) => (
                      <div
                        key={item._id || item.module || idx}
                        className="course-detail-page__syllabus-card"
                      >
                        <div className="course-detail-page__syllabus-head">
                          <h3 className="course-detail-page__syllabus-module">{item.module}</h3>
                          <span className="course-detail-page__syllabus-num">
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                        </div>
                        <p className="course-detail-page__syllabus-content">{item.content}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* SIDEBAR */}
            <aside className="course-detail-page__sidebar">
              <div className="course-detail-page__sticky-card">
                <span className="course-detail-page__sticky-eyebrow">Enroll</span>
                <h3 className="course-detail-page__sticky-title">Course Actions</h3>

                <div className="course-detail-page__sticky-meta">
                  {course.duration && (
                    <div className="course-detail-page__sticky-meta-row">
                      <span className="course-detail-page__sticky-meta-label">Duration</span>
                      <span className="course-detail-page__sticky-meta-value">{course.duration}</span>
                    </div>
                  )}
                  <div className="course-detail-page__sticky-meta-row">
                    <span className="course-detail-page__sticky-meta-label">Projects</span>
                    <span className="course-detail-page__sticky-meta-value">{course.projects || 0}</span>
                  </div>
                  {totalModules > 0 && (
                    <div className="course-detail-page__sticky-meta-row">
                      <span className="course-detail-page__sticky-meta-label">Modules</span>
                      <span className="course-detail-page__sticky-meta-value">{totalModules}</span>
                    </div>
                  )}
                  <div className="course-detail-page__sticky-meta-row">
                    <span className="course-detail-page__sticky-meta-label">Status</span>
                    <span className="course-detail-page__sticky-meta-value">
                      {isEnrolled ? 'Enrolled' : 'Open'}
                    </span>
                  </div>
                </div>

                {message && (
                  <div className="course-detail-page__alert course-detail-page__alert--success">
                    {message}
                  </div>
                )}
                {error && (
                  <div className="course-detail-page__alert course-detail-page__alert--error">
                    {error}
                  </div>
                )}

                {isEnrolled ? (
                  <button
                    onClick={handleUnenroll}
                    disabled={actionLoading}
                    className="course-detail-page__btn course-detail-page__btn--danger"
                  >
                    <span>{actionLoading ? 'Processing…' : 'Unenroll from Course'}</span>
                  </button>
                ) : (
                  <button
                    onClick={handleEnroll}
                    disabled={actionLoading}
                    className="course-detail-page__btn course-detail-page__btn--primary"
                  >
                    <span>{actionLoading ? 'Processing…' : 'Enroll Now'}</span>
                    <svg className="course-detail-page__btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* SANSKRIT MOTIVATION (compact) */}
      <section className="course-detail-page__motivation">
        <div className="course-detail-page__motivation-inner">
          <span className="course-detail-page__motivation-tag">The Call to Mastery</span>
          <h2 className="course-detail-page__shloka">“योगः कर्मसु कौशलम्”</h2>
          <div className="course-detail-page__motivation-divider" />
          <p className="course-detail-page__translation">
            <strong>Yogaḥ karmasu kauśalam.</strong>
          </p>
          <p className="course-detail-page__explanation">
            "Yoga is excellence in action." True mastery is not just knowing —
            it is executing with precision, focus, and elegance.
          </p>
        </div>
      </section>
    </div>
  );
}